// CLT sub-menu of an element (HTML #mat{n}CltFields): insertion side, layers, arrangement, thicknesses, class or the
// user-defined per-layer rows, and the layup summary.
import { memo, type FC } from "react";
import { FormField } from "../../../components/ds2/FormField";
import { MessageBox } from "../../../components/ds2/MessageBox";
import { Numeric } from "../../../components/ds2/Numeric";
import { Select } from "../../../components/ds2/Select";
import { CLT_CLASSI, CLT_LAYER_COUNTS, cltDisposizioniAmmesse } from "../../../http/screwConnector/formulas/clt";
import type {
  CltDisposizione,
  CltLato,
  CltLayerInput,
  CltSpessoriMode,
} from "../../../http/screwConnector/formulas/types";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import { useMissing } from "./missingFields";
import Note from "./Note";
import { numOrNull, stringOptions, type EngineView } from "./uiState";
import { useNumberDraft } from "./useNumberDraft";
import { useScForm } from "./useScForm";
import styles from "./CltFields.module.css";

const DEFAULT_LAYER_THICKNESS = 20;
const DEFAULT_LAYER_CLASS = "C24";

const ORIENTATION_OPTIONS = [
  { value: "0", label: "0°" },
  { value: "90", label: "90°" },
];

type LayerRow = { thickness: number | null; orientation: 0 | 90; classe: string };

// numStrati rows, reusing the existing ones by index (renderCltUserLayers): 20 mm, 0°/90° alternating, C24
function layerRows(layers: readonly LayerRow[], count: number): LayerRow[] {
  return Array.from({ length: count }, (_, i): LayerRow => {
    const layer = layers[i];
    return layer ?? { thickness: DEFAULT_LAYER_THICKNESS, orientation: i % 2 === 0 ? 0 : 90, classe: DEFAULT_LAYER_CLASS };
  });
}

type CltFieldsProps = { n: 1 | 2; view: EngineView };

const CltFields: FC<CltFieldsProps> = ({ n, view }) => {
  const disabled = useReadOnly();
  const t = useI18n().translation.screwConnectorModule.calculation.clt;
  const { values, set } = useScForm();
  const draft = useNumberDraft();
  const isMissing = useMissing();
  const { ui, options } = view;
  const ev = view.values;

  const lato = ev ? ev[`clt${n}Lato` as const] : (values[`element${n}CltLato` as const] ?? "narrow");
  const numStrati = ev ? ev[`clt${n}NumStrati` as const] : (values[`element${n}CltNumStrati` as const] ?? 3);
  const disposizione = ev ? ev[`clt${n}Disposizione` as const] : (values[`element${n}CltDisposizione` as const] ?? "fisso");
  const spessoriMode = ev ? ev[`clt${n}SpessoriMode` as const] : (values[`element${n}CltSpessoriMode` as const] ?? "uniforme");
  const differenziato = spessoriMode === "differenziato";
  const classe = ev ? ev[`clt${n}Classe` as const] : (values[`element${n}CltClasse` as const] ?? DEFAULT_LAYER_CLASS);
  const spessore0 = ev ? numOrNull(ev[`clt${n}Spessore0` as const]) : values[`element${n}CltSpessore0` as const];
  const spessore90 = ev ? numOrNull(ev[`clt${n}Spessore90` as const]) : values[`element${n}CltSpessore90` as const];
  const classi = options.cltClassi.length ? options.cltClassi : CLT_CLASSI;
  const disposizioni = options.cltDisposizione[n].length
    ? options.cltDisposizione[n]
    : cltDisposizioniAmmesse(Number.isFinite(numStrati) ? numStrati : 3);

  const latoOptions = options.cltLato[n].map((value) => ({ value, label: t.latoOptions[value] }));
  const numStratiOptions = stringOptions(CLT_LAYER_COUNTS.map(String));
  const numStratiValue = Number.isFinite(numStrati) ? String(numStrati) : "";
  const disposizioneOptions = disposizioni.map((value) => ({
    value,
    label: t.disposizioni[value as CltDisposizione] ?? value,
  }));
  const spessoriModeOptions = (["uniforme", "differenziato"] as const).map((value) => ({
    value,
    label: t.spessoriModes[value],
  }));
  const classOptions = stringOptions(classi);

  const savedLayers: LayerRow[] = ev
    ? ev[`clt${n}UserLayers` as const].map((l) => ({
        thickness: numOrNull(l.spessore),
        orientation: (l.orientamento === 90 ? 90 : 0) as LayerRow["orientation"],
        classe: l.classe,
      }))
    : (values[`element${n}CltLayers` as const] ?? [])
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((l) => ({ thickness: l.thickness, orientation: l.orientation, classe: l.classe }));
  const rows = layerRows(savedLayers, Number.isFinite(numStrati) ? numStrati : 0);

  const setLayer = (index: number, patch: Partial<LayerRow>) => {
    const next: CltLayerInput[] = rows.map((row, i) => {
      const r = i === index ? { ...row, ...patch } : row;
      // the DB requires thickness > 0: a row never committed keeps the default thickness
      return {
        position: i,
        thickness: r.thickness !== null && r.thickness > 0 ? r.thickness : DEFAULT_LAYER_THICKNESS,
        orientation: r.orientation,
        classe: r.classe,
      };
    });
    set(`element${n}CltLayers` as const, next);
  };

  const info = ui.texts[`clt${n}Info` as const];

  return (
    <div className={styles.stack}>
      <div className={styles.row}>
        <FormField label={t.lato} footer={{ type: "info", message: "ETA-11/0030" }} disabled={disabled}>
          {({ labelId, disabled }) => (
            <>
              <Select
                inputId={labelId}
                name={`clt${n}Lato`}
                options={latoOptions}
                value={latoOptions.find((o) => o.value === lato) ?? null}
                isDisabled={disabled}
                isInvalid={isMissing(`clt${n}Lato`, !lato)}
                isSearchable={false}
                placeholder=""
                onChange={(o) => o && o.value !== lato && set(`element${n}CltLato` as const, o.value as CltLato)}
              />
              {n === 1 && ui.visible.clt1LatoHint ? <Note html={t.latoHint1} /> : null}
              {n === 2 && ui.visible.clt2LatoHint ? <Note html={ui.texts.clt2LatoHint} /> : null}
            </>
          )}
        </FormField>
        <FormField label={t.numStrati} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Select
              inputId={labelId}
              name={`clt${n}NumStrati`}
              options={numStratiOptions}
              value={numStratiOptions.find((o) => o.value === numStratiValue) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing(`clt${n}NumStrati`, !numStratiValue)}
              isSearchable={false}
              placeholder=""
              onChange={(o) => o && o.value !== numStratiValue && set(`element${n}CltNumStrati` as const, Number(o.value))}
            />
          )}
        </FormField>
      </div>
      <FormField label={t.disposizione} disabled={disabled}>
        {({ labelId, disabled }) => (
          <Select
            inputId={labelId}
            name={`clt${n}Disposizione`}
            options={disposizioneOptions}
            value={disposizioneOptions.find((o) => o.value === disposizione) ?? null}
            isDisabled={disabled}
            isInvalid={isMissing(`clt${n}Disposizione`, !disposizione)}
            isSearchable={false}
            placeholder=""
            onChange={(o) =>
              o && o.value !== disposizione && set(`element${n}CltDisposizione` as const, o.value as CltDisposizione)
            }
          />
        )}
      </FormField>
      {ui.visible[`clt${n}SpessoriModeField` as const] ? (
        <FormField label={t.spessoriMode} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Select
              inputId={labelId}
              name={`clt${n}SpessoriMode`}
              options={spessoriModeOptions}
              value={spessoriModeOptions.find((o) => o.value === spessoriMode) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing(`clt${n}SpessoriMode`, !spessoriMode)}
              isSearchable={false}
              placeholder=""
              onChange={(o) =>
                o && o.value !== spessoriMode && set(`element${n}CltSpessoriMode` as const, o.value as CltSpessoriMode)
              }
            />
          )}
        </FormField>
      ) : null}
      {ui.visible[`clt${n}Spessore0RowField` as const] ? (
        <div className={styles.row}>
          <FormField
            label={differenziato ? t.spessore0 : t.spessoreSingle}
            footer={{ type: "info", message: "mm" }}
            disabled={disabled}
          >
            {({ labelId, disabled }) => (
              <Numeric
                id={labelId}
                name={`clt${n}Spessore0`}
                disabled={disabled}
                isInvalid={isMissing(`clt${n}Spessore0`, spessore0 === null)}
                {...draft("spessore0", spessore0, (v) => set(`element${n}CltSpessore0` as const, v))}
              />
            )}
          </FormField>
          {ui.visible[`clt${n}Spessore90Field` as const] ? (
            <FormField label={t.spessore90} footer={{ type: "info", message: "mm" }} disabled={disabled}>
              {({ labelId, disabled }) => (
                <Numeric
                  id={labelId}
                  name={`clt${n}Spessore90`}
                  disabled={disabled}
                  isInvalid={isMissing(`clt${n}Spessore90`, spessore90 === null)}
                  {...draft("spessore90", spessore90, (v) => set(`element${n}CltSpessore90` as const, v))}
                />
              )}
            </FormField>
          ) : null}
        </div>
      ) : null}
      {ui.visible[`clt${n}ClasseField` as const] ? (
        <FormField label={t.classe} footer={{ type: "info", message: "C14–C35" }} disabled={disabled}>
          {({ labelId, disabled }) => (
            <Select
              inputId={labelId}
              name={`clt${n}Classe`}
              options={classOptions}
              value={classOptions.find((o) => o.value === classe) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing(`clt${n}Classe`, !classe)}
              isSearchable={false}
              placeholder=""
              onChange={(o) => o && o.value !== classe && set(`element${n}CltClasse` as const, o.value)}
            />
          )}
        </FormField>
      ) : null}
      {ui.visible[`clt${n}UserLayersWrap` as const] ? (
        <FormField label={t.userLayers} footer={{ type: "info", message: t.userLayersUnit }} disabled={disabled}>
          {({ disabled }) => (
            <div className={styles.layers}>
              {rows.map((row, i) => (
                <div key={i} className={styles.layerRow}>
                  <span>{t.layer(i + 1)}</span>
                  <Numeric
                    name={`clt${n}UserLayers`}
                    width="70px"
                    title={t.layerThickness}
                    disabled={disabled}
                    {...draft(
                      `layer${i}`,
                      row.thickness,
                      (v) => {
                        if (v !== null && v > 0) setLayer(i, { thickness: v });
                      },
                      { validate: (v) => v > 0, allowEmpty: false },
                    )}
                  />
                  <Select
                    aria-label={t.layerDirection}
                    options={ORIENTATION_OPTIONS}
                    value={ORIENTATION_OPTIONS.find((o) => o.value === String(row.orientation)) ?? null}
                    isDisabled={disabled}
                    isSearchable={false}
                    placeholder=""
                    onChange={(o) =>
                      o && o.value !== String(row.orientation) && setLayer(i, { orientation: o.value === "90" ? 90 : 0 })
                    }
                  />
                  <Select
                    aria-label={t.layerClass}
                    options={classOptions}
                    value={classOptions.find((o) => o.value === row.classe) ?? null}
                    isDisabled={disabled}
                    isSearchable={false}
                    placeholder=""
                    onChange={(o) => o && o.value !== row.classe && setLayer(i, { classe: o.value })}
                  />
                </div>
              ))}
            </div>
          )}
        </FormField>
      ) : null}
      {info ? (
        ui.textTone[`clt${n}Info` as const] === "amber" ? (
          <MessageBox type="warning">{info}</MessageBox>
        ) : (
          <Note html={info} />
        )
      ) : null}
    </div>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(CltFields);
